import React, { useState } from "react";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import { Link as RouterLink } from "react-router-dom";
import SecureConnected from "../components/SecureConnected";
import { SiteFooter, SiteHeader } from "../components/SiteChrome";
import useScrollReveal from "../hooks/useScrollReveal";
import "./HelpCenter.css";

const categories = [
  {
    icon: <CreditCardOutlinedIcon />,
    title: "Getting Started",
    copy: "New to trilLOAN? Learn the basics and get up and running quickly.",
    questions: [
      "How do I create a trilLOAN Account?",
      "What documents do I need to get started?",
      "How long does account verification take?",
      "Can i use trilLOAN without a smartphone?",
    ],
  },
  {
    icon: <ManageAccountsOutlinedIcon />,
    title: "Account & Verification",
    copy: "Everything about your account, profile, and verification process.",
    questions: [
      "Why do i need to verify my identity?",
      "My verification was rejected. What should I do?",
      "How do I update my personal information?",
      "Can I have multiple trilLOAN accounts?",
      "How do I delete my account?",
    ],
  },
  {
    icon: <AccountBalanceWalletOutlinedIcon />,
    title: "Borrowing & Lending",
    copy: "Learn how to borrow money or lend to others on our platform.",
    questions: [
      "How do I apply for a loan?",
      "What’s the maximum amount I can borrow?",
      "How do I become a lender?",
      "How are interest rates determined?",
      "What happens if a borrower can’t repay?",
      "Can I pay my loan early?",
    ],
  },
  {
    icon: <CreditCardOutlinedIcon />,
    title: "Payment & Transfers",
    copy: "Managing your money, payments, and fund transfers safely.",
    questions: [
      "How do I add money to my trilLOAN wallet?",
      "What payment methods do you accept?",
      "Are there fees for adding money?",
      "How do i withdraw money from my wallet?",
      "My payment failed. What should I do?",
    ],
  },
  {
    icon: <SecurityOutlinedIcon />,
    title: "Security & Privacy",
    copy: "Keeping your account and personal information safe and secure.",
    questions: [
      "How secure is my money on trilLOAN?",
      "What is end-to-end encryption?",
      "How do I enable biometric login?",
      "What should I do if I suspect fraud?",
      "Do you share my personal information?",
    ],
  },
  {
    icon: <SettingsOutlinedIcon />,
    title: "Troubleshooting",
    copy: "Quick fixes for common issues and technical problems.",
    questions: [
      "The app won’t open or keeps crashing",
      "I forgot my password",
      "I’m not receiving SMS codes",
      "My transaction is taking too long",
      "I can’t find a specific feature",
    ],
  },
];

const supportCards = [
  {
    icon: <TipsAndUpdatesOutlinedIcon />,
    title: "Still need help?",
    copy: "Can't find what you're looking for? Browse our complete knowledge base for more detailed guides.",
    action: "Browse all Articles",
  },
  {
    icon: <ChatBubbleOutlineRoundedIcon />,
    title: "Contact Support",
    copy: "Our friendly support team is available to help you with any questions or concerns.",
    action: "Get in Touch",
    href: "/contact-us",
  },
];

export default function HelpCenter() {
  const [activeCategory, setActiveCategory] = useState(null);
  useScrollReveal();

  return (
    <main className="help-center-page">
      <SiteHeader />

      <section className="help-center-hero tl-reveal">
        <div className="help-center-heading">
          <h1>Help Center</h1>
          <p>
            Find answers to your questions, learn how to use trilLOAN, and get
            the support you need.
          </p>
        </div>

        <div className="help-browser">
          <label className="help-search">
            <SearchRoundedIcon />
            <input
              type="search"
              placeholder="Search for help articles, features, or common questions..."
            />
          </label>

          {activeCategory ? (
            <div className="help-category-detail">
              <button
                className="back-to-categories"
                type="button"
                onClick={() => setActiveCategory(null)}
              >
                <ArrowBackRoundedIcon />
                Back to Categories
              </button>

              <div className="help-detail-heading">
                <span>{activeCategory.icon}</span>
                <div>
                  <h2>{activeCategory.title}</h2>
                  <p>{activeCategory.copy}</p>
                </div>
              </div>

              <div className="help-question-list">
                {activeCategory.questions.map((question) => (
                  <button type="button" key={question}>
                    <h2>{question}</h2>
                    <KeyboardArrowDownRoundedIcon />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <h2>Browse by Category</h2>
              <div className="help-category-grid">
                {categories.map((category, index) => (
                  <button
                    className="help-category-card"
                    type="button"
                    key={category.title}
                    onClick={() => setActiveCategory(category)}
                  >
                    <span>{category.icon}</span>
                    <div>
                      <h3>{category.title}</h3>
                      <p>{category.copy}</p>
                    </div>
                    <ArrowForwardIosRoundedIcon className="help-card-arrow" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="help-support-grid">
          {supportCards.map((card, index) => (
            <article className={`tl-reveal tl-reveal-delay-${index % 2}`} key={card.title}>
              <span>{card.icon}</span>
              <h3>{card.title}</h3>
              <p>{card.copy}</p>
              {card.href ? (
                <RouterLink to={card.href}>{card.action}</RouterLink>
              ) : (
                <button type="button" onClick={() => setActiveCategory(categories[0])}>
                  {card.action}
                </button>
              )}
            </article>
          ))}
        </div>
      </section>

      <SecureConnected />
      <SiteFooter />
    </main>
  );
}
